import { useOpenRedemptionPortal } from '@/src/common/hooks/useOpenRedemptionPortal'
import { type ReactNode, useMemo } from 'react'
import { BookTravel } from './icons/BookTravel'
import { GiftCards } from './icons/GiftCards'
import { History } from './icons/History'
import { Cashback } from './icons/Cashback'
import { useRouter } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import { FeatureGates } from '@/src/common/enums/FeatureGates'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { PointsTransfer } from './icons/PointsTransfer'
import { triggerSegmentAction } from '@/src/services/segment'

export function useRewardOptions() {
  const { openRedemptionPortal } = useOpenRedemptionPortal()
  const router = useRouter()
  const pointRedemptionForStatementCreditEnabled = useFeatureGate(
    FeatureGates.PointRedemptionForStatementCredit,
  ).featureGate?.value
  const pointsTransferComingSoonEnabled = useFeatureGate(
    FeatureGates.PointsTransferComingSoonEnabled,
  ).featureGate?.value
  const pointsTransferAscendaEnabled = useFeatureGate(FeatureGates.PointsTransferAscendaEnabled)
    .featureGate?.value
  const pointsTransferOmniEnabled = useFeatureGate(FeatureGates.PointsTransferOmniEnabled)
    .featureGate?.value

  return useMemo(
    () =>
      [
        {
          title: 'Book Travel',
          icon: <BookTravel width="65%" height="65%" />,
          async onOpen() {
            await triggerSegmentAction({
              component: 'Reward Option',
              action: 'User Opened the Redemption Portal to Book Travel',
            })
            await openRedemptionPortal('Flights', {
              variables: { redirectPath: '/travel/flights' },
            })
          },
        },
        {
          title: 'Gift Cards',
          icon: <GiftCards width="65%" height="65%" />,
          async onOpen() {
            await triggerSegmentAction({
              component: 'Reward Option',
              action: 'User Opened the Redemption Portal to Gift Cards',
            })
            await openRedemptionPortal('Gift Cards', {
              variables: { redirectPath: '/products/gift-cards' },
            })
          },
        },
        ...(pointRedemptionForStatementCreditEnabled
          ? [
              {
                title: 'Statement Credit',
                icon: <Cashback width="65%" height="65%" />,
                async onOpen() {
                  await triggerSegmentAction({
                    component: 'Reward Option',
                    action: 'User Opened the Redemption Portal to Statement Credit',
                  })
                  router.push(Routes.PointsToStatementCredit.route)
                },
              },
            ]
          : []),
        {
          title: 'History',
          icon: <History width="65%" height="65%" />,
          async onOpen() {
            await triggerSegmentAction({
              component: 'Reward Option',
              action: 'User Opened the Redemption Portal to History',
            })
            await openRedemptionPortal('Redemption History', {
              variables: { redirectPath: '/rewards-summary/points-activity' },
            })
          },
        },
        ...(pointsTransferOmniEnabled
          ? [
              {
                title: 'Omni',
                icon: <PointsTransfer width="65%" height="65%" />,
                async onOpen() {
                  await triggerSegmentAction({
                    component: 'Reward Option',
                    action: 'User Opened Points Transfer to Omni',
                  })
                  router.push('/main/points-to-omni/partner-detail')
                },
              },
            ]
          : []),
        ...(pointsTransferComingSoonEnabled || pointsTransferAscendaEnabled
          ? [
              {
                title: 'Points Transfer',
                icon: <PointsTransfer width="65%" height="65%" />,
                onOpen: async () => {
                  if (pointsTransferAscendaEnabled) {
                    await openRedemptionPortal('Points Transfer', {
                      variables: { redirectPath: '/products/frequent-traveler-programs' },
                    })
                  }
                },
                hasComingSoonOverlay: !pointsTransferAscendaEnabled,
              },
            ]
          : []),
      ] satisfies Array<{
        title: string
        icon: ReactNode
        onOpen?: () => void | Promise<void>
        hasComingSoonOverlay?: boolean
      }>,
    [
      openRedemptionPortal,
      pointRedemptionForStatementCreditEnabled,
      pointsTransferComingSoonEnabled,
      router,
      pointsTransferAscendaEnabled,
      pointsTransferOmniEnabled,
    ],
  )
}

export type RewardOption = ReturnType<typeof useRewardOptions>[number]
